import { useRef, useEffect, useCallback } from "react";
import { TextB, TextItalic, TextUnderline, ListBullets, ListNumbers, Link as LinkIcon, TextAlignLeft, TextAlignCenter, TextAlignRight, Eraser } from "@phosphor-icons/react";
import { sanitizeHtml } from "../utils/sanitizeHtml";

/**
 * Lightweight contentEditable rich-text editor.
 * Stores HTML as a string. No external dependency.
 * - Toolbar: B / I / U / UL / OL / Link / Align L-C-R / Clear format
 * - Pastes are converted to plain text to prevent style injection.
 * - Parent controls value via `value` prop; `onChange(html)` fires on input.
 */
export const RichTextEditor = ({
  value = "",
  onChange,
  placeholder = "Start typing…",
  minHeight = 140,
  testId = "rich-text-editor",
  disabled = false,
}) => {
  const ref = useRef(null);

  // Sync external value → DOM only when it differs (avoid caret jump while typing)
  useEffect(() => {
    if (ref.current) {
      const clean = sanitizeHtml(value || "");
      if (ref.current.innerHTML !== clean) {
        ref.current.innerHTML = clean;
      }
    }
  }, [value]);

  const exec = useCallback((cmd, arg) => {
    if (disabled) return;
    document.execCommand(cmd, false, arg);
    if (ref.current) {
      onChange?.(ref.current.innerHTML);
      ref.current.focus();
    }
  }, [disabled, onChange]);

  const handleInput = () => {
    onChange?.(ref.current?.innerHTML || "");
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData("text/plain");
    document.execCommand("insertText", false, text);
  };

  const insertLink = () => {
    const url = window.prompt("Enter URL:");
    if (url) exec("createLink", url);
  };

  const btn = "px-2 py-1 rounded border border-slate-200 hover:bg-slate-100 text-slate-700 disabled:opacity-40 disabled:cursor-not-allowed";

  return (
    <div className="border border-slate-300 rounded overflow-hidden bg-white" data-testid={testId}>
      <div className="flex flex-wrap items-center gap-1 border-b border-slate-200 px-2 py-1.5 bg-slate-50">
        <button type="button" onClick={() => exec("bold")} className={btn} title="Bold" aria-label="Bold" data-testid={`${testId}-bold`} disabled={disabled}><TextB size={14} weight="bold" /></button>
        <button type="button" onClick={() => exec("italic")} className={btn} title="Italic" aria-label="Italic" data-testid={`${testId}-italic`} disabled={disabled}><TextItalic size={14} /></button>
        <button type="button" onClick={() => exec("underline")} className={btn} title="Underline" aria-label="Underline" data-testid={`${testId}-underline`} disabled={disabled}><TextUnderline size={14} /></button>
        <span className="w-px h-5 bg-slate-300 mx-1" />
        <button type="button" onClick={() => exec("insertUnorderedList")} className={btn} title="Bulleted list" aria-label="Bulleted list" data-testid={`${testId}-ul`} disabled={disabled}><ListBullets size={14} /></button>
        <button type="button" onClick={() => exec("insertOrderedList")} className={btn} title="Numbered list" aria-label="Numbered list" data-testid={`${testId}-ol`} disabled={disabled}><ListNumbers size={14} /></button>
        <span className="w-px h-5 bg-slate-300 mx-1" />
        <button type="button" onClick={() => exec("justifyLeft")} className={btn} title="Align left" aria-label="Align left" disabled={disabled}><TextAlignLeft size={14} /></button>
        <button type="button" onClick={() => exec("justifyCenter")} className={btn} title="Align center" aria-label="Align center" disabled={disabled}><TextAlignCenter size={14} /></button>
        <button type="button" onClick={() => exec("justifyRight")} className={btn} title="Align right" aria-label="Align right" disabled={disabled}><TextAlignRight size={14} /></button>
        <span className="w-px h-5 bg-slate-300 mx-1" />
        <button type="button" onClick={insertLink} className={btn} title="Insert link" aria-label="Insert link" disabled={disabled}><LinkIcon size={14} /></button>
        <button type="button" onClick={() => exec("removeFormat")} className={btn} title="Clear formatting" aria-label="Clear formatting" disabled={disabled}><Eraser size={14} /></button>
        <span className="ml-auto text-[10px] text-slate-400 uppercase tracking-wider pr-1">HTML</span>
      </div>
      <div
        ref={ref}
        contentEditable={!disabled}
        suppressContentEditableWarning
        onInput={handleInput}
        onBlur={handleInput}
        onPaste={handlePaste}
        data-placeholder={placeholder}
        className="rte-content px-3 py-2 text-sm outline-none focus:bg-slate-50/40 whitespace-pre-wrap"
        style={{ minHeight }}
      />
      <style>{`
        .rte-content:empty::before { content: attr(data-placeholder); color: #94a3b8; pointer-events: none; }
        .rte-content ul { list-style: disc; padding-left: 1.25rem; margin: 0.25rem 0; }
        .rte-content ol { list-style: decimal; padding-left: 1.5rem; margin: 0.25rem 0; }
        .rte-content a { color: #002FA7; text-decoration: underline; }
        .rte-content p { margin: 0.25rem 0; }
        .rte-content b, .rte-content strong { font-weight: 700; }
      `}</style>
    </div>
  );
};

export default RichTextEditor;
